import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  Circle,
  Flex,
  HStack,
  Icon,
  Stack,
  Tag,
  Text,
  Tooltip
} from '@chakra-ui/react';
import { IoGridOutline, IoStorefrontOutline } from 'react-icons/io5';
import { RiAuctionLine } from 'react-icons/ri';
import { ImStatsDots } from 'react-icons/im';
import { MdPendingActions, MdMergeType, MdOutlineGroupWork, MdImage, MdCheck, MdClose, MdSelectAll } from 'react-icons/md';
import { BsSearch } from 'react-icons/bs';
import {
  ArtistBanner,
  ArtistBannerSkeleton,
  EmptyState,
  InfoGuide,
  ItemGrid,
  PaginatedListingGrid,
  TabGroup,
  TradingFloor,
  Modal,
  CircularIconButton,
  BulkEditButton,
  ArtistDataSuggestionForm,
  GroupingCreationWizard,
  ListingDataSuggestionWizard,
  ListingTagWizard,
  ListingSearchFiltersForm,
  MergeCreationWizard,
  MigrateImageWizard
} from 'components';
import { ListingTagType } from 'components/Wizard/ListingTagWizard/ListingTagWizard';
import { ListingSearchFiltersFormData } from 'components/Form/ListingSearchFiltersForm/ListingSearchFiltersForm';
import { ItemModel, ListingModel, PaginationModel, ArtistModel } from 'models';
import { useArtist, useArtistActiveMarket, useArtistEditionIndex, useArtistItems, useArtistListings, useUpdateArtist, useRegenerateArtistIndex, useRefreshArtistIndex, useMigrateArtistImage } from 'hooks/queries/useArtists';
import { NavigationService } from 'services';
import { getPageFromURL, getSelectedTabFromURL } from 'utils/url';
import { QuerySearchParameters } from 'constants/url';
import { useAuth } from 'contexts/AuthContext';
import ArtistIndexChart from 'components/Charts/ArtistIndexChart';
import { JSONObject } from 'models/Api/types';
import { ArtistDataSuggestionFormData } from 'components/Form/ArtistDataSuggestionForm/ArtistDataSuggestionForm';
import { VscEdit } from 'react-icons/vsc';
import { useArtistPendingListings, useMarkListingAsFake, useMarkListingAsOriginal, useMarkListingAsSet } from 'hooks/queries/useListings';
import { useListingSearch } from 'hooks/queries/useSearch';
import { ListingStatus } from 'models/Listing/types';
import { useCreateGrouping } from 'hooks/queries/useGroupings';
import { useCreateMerge } from 'hooks/queries/useMerges';
import { useQueryClient } from '@tanstack/react-query';
import { ArtistIndexType } from 'models/Artist/ArtistIndex';

type ListingsUpdateMode = 'replace' | 'append';

interface ArtistDetailsModalState {
  editArtist: boolean;
  linkListing: ListingModel | null;
  tagListing: ListingModel | null;
  tagAction: 'FAKE' | 'SET' | 'ORIGINAL' | null;
  createMerge: boolean;
  createGrouping: boolean;
  migrateImage: boolean;
}

export default function ArtistDetailsView() {
  const { id: artistId = '' } = useParams<{ slug?: string; id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const skipNextPageSync = useRef(false);
  const [modals, setModals] = useState<ArtistDetailsModalState>({
    editArtist: false,
    linkListing: null,
    tagListing: null,
    tagAction: null,
    createMerge: false,
    createGrouping: false,
    migrateImage: false
  });

  const pageFromQuery = useMemo(() => {
    const page = getPageFromURL(location);
    return page > 0 ? page : 1;
  }, [location.search]);

  const defaultQueryParamValues = useMemo<Partial<Record<QuerySearchParameters, string>>>(
    () => ({
      [QuerySearchParameters.PAGE]: '1',
      [QuerySearchParameters.TAB]: '0',
    }),
    []
  );

  const [listingPage, setListingPage] = useState<number>(pageFromQuery);
  const [listingUpdateMode, setListingUpdateMode] = useState<ListingsUpdateMode>('replace');
  const [soldListings, setSoldListings] = useState<PaginationModel<ListingModel>>(
    () => new PaginationModel<ListingModel>()
  );
  const [pendingPage, setPendingPage] = useState<number>(1);
  const [pendingUpdateMode, setPendingUpdateMode] = useState<ListingsUpdateMode>('replace');
  const [pendingListings, setPendingListings] = useState<PaginationModel<ListingModel>>(
    () => new PaginationModel<ListingModel>()
  );

  // Pending (untagged) sales selection state
  const [pendingSelectionMode, setPendingSelectionMode] = useState(false);
  const [selectedPendingListings, setSelectedPendingListings] = useState<ListingModel[]>([]);
  const [pendingConfirmAction, setPendingConfirmAction] = useState<ListingTagType | null>(null);
  const [pendingConfirmModalOpen, setPendingConfirmModalOpen] = useState(false);

  // Pending (untagged) sales search state
  const [pendingSearchModalOpen, setPendingSearchModalOpen] = useState(false);
  const [pendingListingSearchFilters, setPendingListingSearchFilters] = useState<ListingSearchFiltersFormData | undefined>(undefined);
  const [pendingListingSearchPage, setPendingListingSearchPage] = useState(1);

  const { data: artist, isLoading: artistLoading } = useArtist(artistId);
  const { data: artistItems = [], isLoading: artistItemsLoading } = useArtistItems(artistId);
  const {
    data: listingsForPage,
    isLoading: artistListingsLoading,
    isFetching: artistListingsFetching
  } = useArtistListings(artistId, listingPage);
  const {
    data: pendingListingsPageData,
    isLoading: pendingListingsLoading,
    isFetching: pendingListingsFetching
  } = useArtistPendingListings(artistId, pendingPage);
  const {
    data: pendingListingSearchResults,
    isLoading: pendingListingSearchLoading
  } = useListingSearch(pendingListingSearchFilters, pendingListingSearchPage);
  const { data: artistIndex, isLoading: artistIndexLoading, isFetching: artistIndexFetching } = useArtistEditionIndex(artistId);
  const regenerateArtistIndexMutation = useRegenerateArtistIndex();
  const refreshArtistIndexMutation = useRefreshArtistIndex();
  const { data: activeMarket, isLoading: artistActiveMarketLoading } = useArtistActiveMarket(artistId);
  const updateArtistMutation = useUpdateArtist();
  const migrateImageMutation = useMigrateArtistImage();
  const queryClient = useQueryClient();
  const createGroupingMutation = useCreateGrouping({ navigateOnSuccess: false });
  const createMergeMutation = useCreateMerge();
  const markAsFakeMutation = useMarkListingAsFake();
  const markAsOriginalMutation = useMarkListingAsOriginal();
  const markAsSetMutation = useMarkListingAsSet();

  const isListingsLoading = artistListingsLoading || artistListingsFetching;
  const isPendingListingsLoading = pendingListingsLoading || pendingListingsFetching;
  const isArtistIndexUpdating = regenerateArtistIndexMutation.isPending || refreshArtistIndexMutation.isPending;
  const isArtistStatsLoading = artistIndexLoading || artistIndexFetching || isArtistIndexUpdating;
  const currentArtistId = artist?.id || artistId;

  const handleRegenerateArtistIndex = useCallback(() => {
    if (isArtistIndexUpdating || !currentArtistId) return;
    regenerateArtistIndexMutation.mutate({ artistId: currentArtistId, type: ArtistIndexType.EDITION });
  }, [currentArtistId, isArtistIndexUpdating, regenerateArtistIndexMutation]);

  const handleRefreshArtistIndex = useCallback(() => {
    if (isArtistIndexUpdating || !currentArtistId) return;
    refreshArtistIndexMutation.mutate({ artistId: currentArtistId, type: ArtistIndexType.EDITION });
  }, [currentArtistId, isArtistIndexUpdating, refreshArtistIndexMutation]);

  const initialTab = useMemo(() => getSelectedTabFromURL(location), [location]);

  const artistFormDefaults: ArtistDataSuggestionFormData = useMemo(() => {
    if (!artist) {
      return undefined;
    }

    const birthYear = artist.birthYear && artist.birthYear > 0 ? artist.birthYear : undefined;

    return {
      name: artist.name || undefined,
      birthYear,
      country: artist.country?.code ? { code: artist.country.code, name: artist.country.name } : undefined,
      artsy: artist.contactInfo.artsyLink || undefined,
      instagram: artist.contactInfo.instagramLink || undefined,
      website: artist.contactInfo.website || undefined,
      description: artist.description || undefined,
      representativeGallery: artist.contactInfo.representativeGallery || undefined,
    };
  }, [artist]);

  const updateQueryParam = useCallback(
    (param: QuerySearchParameters, value: string) => {
      const searchParams = new URLSearchParams(location.search);
      const currentValue = searchParams.get(param);
      if (currentValue === value) {
        return;
      }

      const defaultValue = defaultQueryParamValues[param];

      if (defaultValue !== undefined && value === defaultValue) {
        searchParams.delete(param);
      } else {
        searchParams.set(param, value);
      }

      const queryString = searchParams.toString();
      const nextUrl = queryString.length ? `${location.pathname}?${queryString}` : location.pathname;
      skipNextPageSync.current = true;
      navigate(nextUrl, { replace: true });
    },
    [defaultQueryParamValues, location.pathname, location.search, navigate]
  );

  useEffect(() => {
    if (!artist || !listingsForPage) return;

    setSoldListings((previousListings) => {
      const nextListings = PaginationModel.deepCopy(listingsForPage);
      if (listingUpdateMode === 'append' && previousListings?.hasData()) {
        nextListings.setData([...previousListings.data, ...nextListings.data]);
      }
      return nextListings;
    });

    if (listingUpdateMode === 'append') {
      setListingUpdateMode('replace');
    }
  }, [artist, listingsForPage, listingUpdateMode]);

  useEffect(() => {
    if (!pendingListingsPageData) return;

    setPendingListings((previousListings) => {
      const nextListings = PaginationModel.deepCopy(pendingListingsPageData);
      let combinedData = nextListings.data;

      if (pendingUpdateMode === 'append' && previousListings?.hasData()) {
        combinedData = [...previousListings.data, ...combinedData];
      }

      nextListings.setData(combinedData);
      return nextListings;
    });

    if (pendingUpdateMode === 'append') {
      setPendingUpdateMode('replace');
    }
  }, [pendingListingsPageData, pendingUpdateMode]);

  useEffect(() => {
    if (skipNextPageSync.current) {
      skipNextPageSync.current = false;
      return;
    }

    // Don't sync if we're in append mode (infinite scroll)
    if (listingUpdateMode === 'append') {
      return;
    }

    if (pageFromQuery !== listingPage) {
      setListingUpdateMode('replace');
      setListingPage(pageFromQuery);
    }
  }, [pageFromQuery, listingPage, listingUpdateMode]);

  useEffect(() => {
    if (artist && artistItems) {
      artist.setItems(artistItems);
    }
  }, [artist, artistItems]);

  useEffect(() => {
    if (artist && soldListings) {
      artist.setSoldListings(soldListings);
    }
  }, [artist, soldListings]);

  useEffect(() => {
    if (artist && artistIndex) {
      artist.setEditionIndex(artistIndex);
    }
  }, [artist, artistIndex]);

  useEffect(() => {
    if (artist && activeMarket) {
      artist.setActiveMarket(activeMarket);
    }
  }, [artist, activeMarket]);

  const handleItemCardClick = useCallback(
    (itemId: string, itemName: string) => {
      navigate(NavigationService.getItemDetailsPath(itemId, itemName));
    },
    [navigate]
  );

  const handleItemGridCardClick = (item: ItemModel) => {
    handleItemCardClick(item.id, item.getDisplayName());
  };

  const handleTabChange = (index: number) => {
    updateQueryParam(QuerySearchParameters.TAB, index.toString());
  };

  const handleItemsPageChange = (page: number) => {
    updateQueryParam(QuerySearchParameters.PAGE, page.toString());
  };


  const handleListingsPageSelect = (page: number) => {
    if (!artist) return;

    setListingUpdateMode('replace');
    setListingPage(page);
    updateQueryParam(QuerySearchParameters.PAGE, page.toString());
  };

  const handleListingNameClick = (listingId: string, listingName: string) => {
    navigate(NavigationService.getListingDetailsPath(listingId, listingName));
  };

  const handlePendingListingsPageSelect = (page: number) => {
    setPendingUpdateMode('replace');
    setPendingPage(page);
  };

  // Pending listings search handlers
  const openPendingListingSearchModal = () => setPendingSearchModalOpen(true);
  const closePendingListingSearchModal = () => setPendingSearchModalOpen(false);

  const handlePendingListingSearchSubmit = (searchFilters: ListingSearchFiltersFormData) => {
    // Ensure we always include the artist ID and pending processing status
    const filtersWithArtistAndStatus: ListingSearchFiltersFormData = {
      ...searchFilters,
      artistId: [artistId],
      processingStatus: [ListingStatus.PENDING]
    };
    setPendingListingSearchFilters(filtersWithArtistAndStatus);
    setPendingListingSearchPage(1);
    closePendingListingSearchModal();
  };

  const handlePendingListingSearchPageSelect = (page: number) => {
    setPendingListingSearchPage(page);
  };

  const onLinkListingOpen = (listing: ListingModel) => {
    createGroupingMutation.reset();
    setModals((prev) => ({ ...prev, linkListing: listing }));
  };

  const onLinkListingClose = () => {
    createGroupingMutation.reset();
    setModals((prev) => ({ ...prev, linkListing: null }));
  };

  const onTagListingOpen = (listing: ListingModel, action?: 'FAKE' | 'SET' | 'ORIGINAL') => {
    markAsFakeMutation.reset();
    markAsOriginalMutation.reset();
    markAsSetMutation.reset();
    setModals((prev) => ({ ...prev, tagListing: listing, tagAction: action ?? null }));
  };

  const onTagListingClose = () => {
    setModals((prev) => ({ ...prev, tagListing: null, tagAction: null }));
  };

  const invalidateArtistListingQueries = () => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'artists' && key[1] === artistId;
      }
    });
  };

  const handleLinkListingSubmit = (item: ItemModel, listings: ListingModel[]) => {
    if (!listings.length) {
      return;
    }

    createGroupingMutation.mutate(
      {
        item_id: item.id,
        listings: listings.map((listing) => listing.id),
        auto_link: true
      },
      {
        onSuccess: () => {
          invalidateArtistListingQueries();
          onLinkListingClose();
        },
      }
    );
  };

  const handleCreateGroupingSubmit = (item: ItemModel, listings: ListingModel[]) => {
    if (!listings.length) {
      return;
    }

    createGroupingMutation.mutate(
      {
        item_id: item.id,
        listings: listings.map((listing) => listing.id),
        auto_link: true
      },
      {
        onSuccess: () => {
          invalidateArtistListingQueries();
          onGroupingModalClose();
        },
      }
    );
  };

  const handleTagListingSubmit = (listings: ListingModel[], tagType: ListingTagType, data: JSONObject) => {
    const listingIds = listings.map(l => l.id);
    const onSuccess = () => {
      invalidateArtistListingQueries();
      onTagListingClose();
    };

    if (tagType === 'FAKE') {
      markAsFakeMutation.mutate(listingIds, { onSuccess });
    } else if (tagType === 'SET') {
      markAsSetMutation.mutate(listingIds, { onSuccess });
    } else if (tagType === 'ORIGINAL') {
      markAsOriginalMutation.mutate(listingIds, { onSuccess });
    }
  };

  // Pending (untagged) sales bulk tagging via selection mode
  const handleEnterPendingSelectionMode = useCallback(() => {
    if (!pendingListings?.hasData()) return;
    setPendingSelectionMode(true);
    setSelectedPendingListings([]);
  }, [pendingListings]);

  const handleExitPendingSelectionMode = useCallback(() => {
    setPendingSelectionMode(false);
    setSelectedPendingListings([]);
    setPendingConfirmAction(null);
  }, []);

  const handlePendingListingSelect = useCallback((listing: ListingModel) => {
    setSelectedPendingListings(prev => {
      const exists = prev.some(l => l.id === listing.id);
      if (exists) {
        return prev.filter(l => l.id !== listing.id);
      }
      return [...prev, listing];
    });
  }, []);

  const handlePendingSelectAll = useCallback(() => {
    const visiblePending = pendingListings?.data || [];
    setSelectedPendingListings(visiblePending);
  }, [pendingListings]);

  const handlePendingBulkTagConfirm = useCallback((tagType: ListingTagType) => {
    if (selectedPendingListings.length === 0) return;
    setPendingConfirmAction(tagType);
    setPendingConfirmModalOpen(true);
  }, [selectedPendingListings.length]);

  const executePendingBulkTag = useCallback(() => {
    if (!pendingConfirmAction || selectedPendingListings.length === 0) return;

    const listingIds = selectedPendingListings.map(l => l.id);
    const onSuccess = () => {
      invalidateArtistListingQueries();
      setPendingConfirmModalOpen(false);
      handleExitPendingSelectionMode();
    };

    if (pendingConfirmAction === 'FAKE') {
      markAsFakeMutation.mutate(listingIds, { onSuccess });
    } else if (pendingConfirmAction === 'SET') {
      markAsSetMutation.mutate(listingIds, { onSuccess });
    } else if (pendingConfirmAction === 'ORIGINAL') {
      markAsOriginalMutation.mutate(listingIds, { onSuccess });
    }
  }, [
    pendingConfirmAction,
    selectedPendingListings,
    markAsFakeMutation,
    markAsSetMutation,
    markAsOriginalMutation,
    invalidateArtistListingQueries,
    handleExitPendingSelectionMode
  ]);

  const onArtistEditModalOpen = () => {
    setModals(prev => ({ ...prev, editArtist: true }));
  };

  const onArtistEditModalClose = () => {
    setModals(prev => ({ ...prev, editArtist: false }));
  };

  const onMergeModalOpen = () => {
    createMergeMutation.reset();
    setModals(prev => ({ ...prev, createMerge: true }));
  };

  const onMergeModalClose = () => {
    createMergeMutation.reset();
    setModals(prev => ({ ...prev, createMerge: false }));
  };

  const onGroupingModalOpen = () => {
    createGroupingMutation.reset();
    setModals(prev => ({ ...prev, createGrouping: true }));
  };

  const onGroupingModalClose = () => {
    createGroupingMutation.reset();
    setModals(prev => ({ ...prev, createGrouping: false }));
  };

  const onMigrateImageModalOpen = () => {
    migrateImageMutation.reset();
    setModals(prev => ({ ...prev, migrateImage: true }));
  };

  const onMigrateImageModalClose = () => {
    migrateImageMutation.reset();
    setModals(prev => ({ ...prev, migrateImage: false }));
  };

  const handleMergeSubmit = (selectedItems: ItemModel[]) => {
    if (selectedItems.length < 2) {
      return;
    }

    const payload = {
      items: selectedItems.map(item => item.id)
    };

    createMergeMutation.mutate(payload, {
      onSuccess: () => {
        onMergeModalClose();
      }
    });
  };

  const handleArtistEditSubmit = (formData: ArtistDataSuggestionFormData) => {
    if (!artist) {
      onArtistEditModalClose();
      return;
    }

    if (!formData || Object.keys(formData).length === 0) {
      onArtistEditModalClose();
      return;
    }

    const payload: JSONObject = {};

    if (formData.name !== undefined) {
      payload.name = formData.name;
    }

    if (formData.birthYear !== undefined) {
      payload.birth_year = formData.birthYear;
    }

    if (formData.description !== undefined) {
      payload.description = formData.description;
    }

    if (formData.image !== undefined) {
      payload.image = formData.image;
    }

    if (formData.country !== undefined) {
      payload.country = {
        code: formData.country.code,
        name: formData.country.name,
      };
    }

    const contactInfo: JSONObject = {};
    if (formData.artsy !== undefined) {
      contactInfo.artsy_link = formData.artsy;
    }

    if (formData.instagram !== undefined) {
      contactInfo.instagram_link = formData.instagram;
    }

    if (formData.website !== undefined) {
      contactInfo.website = formData.website;
    }

    if (formData.representativeGallery !== undefined) {
      contactInfo.representative_gallery = formData.representativeGallery;
    }

    if (Object.keys(contactInfo).length > 0) {
      payload.contact_info = contactInfo;
    }

    if (Object.keys(payload).length === 0) {
      onArtistEditModalClose();
      return;
    }

    updateArtistMutation.mutate(
      { id: artist.id, data: payload },
      {
        onSuccess: () => {
          onArtistEditModalClose();
        }
      }
    );
  };

  const handleMigrateImageSubmit = (entity: ItemModel | ListingModel | ArtistModel, newImageUrl: string) => {
    if (!artist) {
      onMigrateImageModalClose();
      return;
    }

    migrateImageMutation.mutate(
      { id: artist.id, imageUrl: newImageUrl },
      {
        onSuccess: () => {
          onMigrateImageModalClose();
        }
      }
    );
  };

  const renderBanner = () => {
    if (artistLoading) {
      return <ArtistBannerSkeleton />;
    }

    if (!artist) {
      return (
        <EmptyState
          header="Artist Not Found"
          description="We could not find an artist with the provided identifier."
          showButton={false}
        />
      );
    }

    return (
      <Box position="relative">
        <Flex position="absolute" top={4} right={4} gap={2}>
          <Tooltip label="Create Merge">
            <Box>
              <CircularIconButton
                icon={MdMergeType}
                onClick={onMergeModalOpen}
                isLoading={createMergeMutation.isPending}
              />
            </Box>
          </Tooltip>
          <Tooltip label="Create Grouping">
            <Box>
              <CircularIconButton
                icon={MdOutlineGroupWork}
                onClick={onGroupingModalOpen}
                isLoading={createGroupingMutation.isPending}
              />
            </Box>
          </Tooltip>
          <Tooltip label="Migrate Image">
            <Box>
              <CircularIconButton
                icon={MdImage}
                onClick={onMigrateImageModalOpen}
                isLoading={migrateImageMutation.isPending}
              />
            </Box>
          </Tooltip>
          <Tooltip label="Edit Artist">
            <Box>
              <CircularIconButton
                icon={VscEdit}
                onClick={onArtistEditModalOpen}
                isLoading={updateArtistMutation.isPending}
              />
            </Box>
          </Tooltip>
        </Flex>
        <ArtistBanner artist={artist} showFollowButton={false} />
      </Box>
    );
  };

  const renderArtistItems = () => {
    if (!artist) {
      return <EmptyState header="Artist Unavailable" description="Artist data is unavailable." showButton={false} />;
    }

    return (
      <ItemGrid
        items={artist.getItems()}
        isLoading={artistItemsLoading}
        onItemCardClick={handleItemGridCardClick}
        paginate={true}
        startingPage={pageFromQuery}
        onPageChange={handleItemsPageChange}
        showMetadata={true}
        defaultMetadataExpanded={true}
      />
    );
  };

  const renderArtistTradingFloor = () => {
    if (!artist) {
      return (
        <EmptyState header="No Active Market" description="Active market data is unavailable for this artist." showButton={false} />
      );
    }

    return (
      <TradingFloor
        bids={artist.activeMarket?.bids || []}
        asks={artist.activeMarket?.asks || []}
        user={currentUser}
        showSearchFilter={true}
        onItemNameClick={(itemId, itemName) => handleItemCardClick(itemId, itemName)}
        isLoading={artistLoading || artistActiveMarketLoading}
      />
    );
  };

  const renderArtistListings = () => {
    if (!artist) {
      return <EmptyState header="Artist Unavailable" description="Artist data is unavailable." showButton={false} />;
    }

    return (
      <Stack spacing={4}>
        <InfoGuide text="Note: We only show publicly available, verifiable auction house sales data." dismissable={true} />
        <PaginatedListingGrid
          paginatedListings={soldListings}
          scrollMode={false}
          onPageSelect={handleListingsPageSelect}
          isLoading={isListingsLoading}
          onListingItemClick={(itemId, itemName) => handleItemCardClick(itemId, itemName)}
          onListingNameClick={handleListingNameClick}
          showAssociationAction={true}
          onItemSuggestionClick={onLinkListingOpen}
          onTagListingClick={onTagListingOpen}
          itemSuggestionLabel="Link Item"
        />
      </Stack>
    );
  };

  const renderArtistPendingListings = () => {
    if (!artist) {
      return <EmptyState header="Artist Unavailable" description="Artist data is unavailable." showButton={false} />;
    }

    // Use search results if search is active, otherwise use regular pending listings
    const isSearchActive = !!pendingListingSearchFilters;
    const displayedListings = isSearchActive ? pendingListingSearchResults : pendingListings;
    const displayedLoading = isSearchActive ? pendingListingSearchLoading : isPendingListingsLoading;
    const pageSelectHandler = isSearchActive ? handlePendingListingSearchPageSelect : handlePendingListingsPageSelect;

    const hasPendingData = displayedListings?.hasData();
    const pendingVisibleCount = displayedListings?.data?.length || 0;
    const allPendingSelected = selectedPendingListings.length === pendingVisibleCount && pendingVisibleCount > 0;
    const tagMutationPending =
      markAsFakeMutation.isPending ||
      markAsOriginalMutation.isPending ||
      markAsSetMutation.isPending;

    return (
      <>
        <Stack spacing={4}>
          <InfoGuide text="Pending sales awaiting review and linking." />
          {(!hasPendingData && !displayedLoading) ? (
            <EmptyState header="No Untagged Sales" description="There are no pending sales awaiting tagging." showButton={false} />
          ) : (
            <PaginatedListingGrid
              paginatedListings={displayedListings}
              scrollMode={false}
              onPageSelect={pageSelectHandler}
              isLoading={displayedLoading}
              onListingClick={pendingSelectionMode ? handlePendingListingSelect : undefined}
              selectedListingIds={pendingSelectionMode ? selectedPendingListings.map(l => l.id) : []}
              onListingItemClick={pendingSelectionMode ? undefined : (itemId, itemName) => handleItemCardClick(itemId, itemName)}
              onListingNameClick={pendingSelectionMode ? undefined : handleListingNameClick}
              showAssociatedItems={true}
              showAssociationAction={true}
              onItemSuggestionClick={pendingSelectionMode ? undefined : onLinkListingOpen}
              onTagListingClick={pendingSelectionMode ? undefined : onTagListingOpen}
              itemSuggestionLabel="Link Item"
            />
          )}
        </Stack>

        <BulkEditButton
          onClick={handleEnterPendingSelectionMode}
          isVisible={!pendingSelectionMode && hasPendingData}
        />
        {/* Search Button */}
        {!pendingSelectionMode && (
          <Circle
            size="40px"
            bg="gray.300"
            left="55%"
            transform="translateX(-50%)"
            position="fixed"
            bottom={100}
            cursor="pointer"
            onClick={openPendingListingSearchModal}
            zIndex={98}
          >
            <Icon as={BsSearch} color="white.100" />
          </Circle>
        )}

        {/* Selection Bar */}
        {pendingSelectionMode && (
          <Box position="fixed" bottom="24px" left="50%" transform="translateX(-50%)" zIndex={99}>
            <Card borderWidth="1px" shadow="lg" borderRadius="lg">
              <CardBody py={3} px={4}>
                <HStack spacing={4}>
                  <Text fontWeight="medium" minW="100px">
                    {selectedPendingListings.length} selected
                  </Text>
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<MdSelectAll />}
                    onClick={handlePendingSelectAll}
                    isDisabled={tagMutationPending || allPendingSelected}
                  >
                    {allPendingSelected ? 'All Selected' : 'Select All'}
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="pink"
                    onClick={() => handlePendingBulkTagConfirm('ORIGINAL')}
                    isDisabled={selectedPendingListings.length === 0 || tagMutationPending}
                  >
                    Original
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="teal"
                    onClick={() => handlePendingBulkTagConfirm('SET')}
                    isDisabled={selectedPendingListings.length === 0 || tagMutationPending}
                  >
                    Set
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="red"
                    onClick={() => handlePendingBulkTagConfirm('FAKE')}
                    isDisabled={selectedPendingListings.length === 0 || tagMutationPending}
                  >
                    Fake
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<MdClose />}
                    onClick={handleExitPendingSelectionMode}
                    isDisabled={tagMutationPending}
                  >
                    Cancel
                  </Button>
                </HStack>
              </CardBody>
            </Card>
          </Box>
        )}

        {/* Search Modal */}
        <Modal
          isOpen={pendingSearchModalOpen}
          onClose={closePendingListingSearchModal}
          title="Search Untagged Sales"
          content={
            <ListingSearchFiltersForm
              defaultFormData={pendingListingSearchFilters}
              onSubmit={handlePendingListingSearchSubmit}
              displayOptions={['query', 'date', 'rawPrice', 'type', 'sorting']}
            />
          }
          showCloseButton={false}
          showSubmitButton={false}
          size="2xl"
        />
      </>
    );
  };

  const renderArtistStats = () => {
    if (!artist) {
      return <EmptyState header="Artist Unavailable" description="Artist data is unavailable." showButton={false} />;
    }

    const editionIndexData = artist.getEditionIndex();

    if (!editionIndexData && !isArtistStatsLoading) {
      return (
        <EmptyState
          header="No Index Data"
          description="We could not find edition index data for this artist."
          showButton={false}
        />
      );
    }

    return (
      <Stack padding={4} spacing={4}>
        <InfoGuide
          text={
            <Text>
              Note: Our edition index does not consider data for{' '}
              <Tag size="sm" variant="solid" colorScheme="pink" marginX={1}>
                Original
              </Tag>
              or
              <Tag size="sm" variant="solid" colorScheme="teal" marginX={1}>
                Set
              </Tag>
              sales.
            </Text>
          }
          dismissable={true}
        />
        {editionIndexData && (
          <ArtistIndexChart
            artist={artist}
            artistIndexData={editionIndexData}
            showFilters={true}
            showMetricCard={true}
            showLastCalculationDate={true}
            onRefreshClick={handleRefreshArtistIndex}
            onRegenerateClick={handleRegenerateArtistIndex}
            isRefreshing={refreshArtistIndexMutation.isPending}
            isRegenerating={regenerateArtistIndexMutation.isPending}
          />
        )}
      </Stack>
    );
  };

  const renderArtistEditModalContent = () => {
    if (!artist) return <Box />;

    return (
      <ArtistDataSuggestionForm
        artist={artist}
        defaultFormData={artistFormDefaults}
        onSubmit={handleArtistEditSubmit}
        submitLoading={updateArtistMutation.isPending}
        displayOptions={['name', 'birthYear', 'description', 'country', 'artsy', 'instagram', 'website', 'representativeGallery']}
      />
    );
  };

  const renderLinkListingModalContent = () => {
    if (!modals.linkListing || !artist) return <Box />;

    return (
      <GroupingCreationWizard
        defaultArtistId={artist.id}
        defaultListings={[modals.linkListing]}
        defaultItem={modals.linkListing.getAssociatedItem() || null}
        preselectedListings={[modals.linkListing]}
        initialWorkflow="listing-first"
        onSubmit={handleLinkListingSubmit}
        isLoading={createGroupingMutation.isPending}
        itemSuggestions={artist.getItems()}
      />
    );
  };

  const renderTagListingModalContent = () => {
    if (!modals.tagListing) return <Box />;

    const tagMutationPending =
      markAsFakeMutation.isPending ||
      markAsOriginalMutation.isPending ||
      markAsSetMutation.isPending;

    return (
      <ListingTagWizard
        listings={[modals.tagListing]}
        preselectedListings={[modals.tagListing]}
        initialTagType={modals.tagAction || undefined}
        onSubmit={handleTagListingSubmit}
        isLoading={tagMutationPending}
      />
    );
  };

  const renderMergeModalContent = () => {
    if (!artist) return <Box />;

    return (
      <MergeCreationWizard
        defaultArtistId={artist.id}
        onSubmit={handleMergeSubmit}
        isLoading={createMergeMutation.isPending}
      />
    );
  };

  const renderGroupingModalContent = () => {
    if (!artist) return <Box />;

    return (
      <GroupingCreationWizard
        defaultArtistId={artist.id}
        initialWorkflow="item-first"
        onSubmit={handleCreateGroupingSubmit}
        isLoading={createGroupingMutation.isPending}
      />
    );
  };

  const renderMigrateImageModalContent = () => {
    if (!artist) return <Box />;

    return (
      <MigrateImageWizard
        entity={artist}
        entityType="artist"
        onSubmit={handleMigrateImageSubmit}
        isLoading={migrateImageMutation.isPending}
        hasErrors={migrateImageMutation.isError}
      />
    );
  };

  const renderPendingConfirmModalContent = () => {
    if (!pendingConfirmAction) return <Box />;

    const tagMutationPending =
      markAsFakeMutation.isPending ||
      markAsOriginalMutation.isPending ||
      markAsSetMutation.isPending;

    const handlePendingTagSubmit = (listings: ListingModel[], tagType: ListingTagType, data: JSONObject) => {
      executePendingBulkTag();
    };

    return (
      <ListingTagWizard
        listings={selectedPendingListings}
        preselectedListings={selectedPendingListings}
        initialTagType={pendingConfirmAction}
        onSubmit={handlePendingTagSubmit}
        isLoading={tagMutationPending}
      />
    );
  };

  const renderModals = () => (
    <>
      <Modal
        isOpen={modals.editArtist}
        onClose={onArtistEditModalClose}
        title="Edit Artist"
        content={renderArtistEditModalContent()}
        showCloseButton={false}
        showSubmitButton={false}
      />
      <Modal
        isOpen={modals.createMerge}
        onClose={onMergeModalClose}
        title="Create Merge"
        content={renderMergeModalContent()}
        showCloseButton={false}
        showSubmitButton={false}
        size="4xl"
      />
      <Modal
        isOpen={modals.createGrouping}
        onClose={onGroupingModalClose}
        title="Create Grouping"
        content={renderGroupingModalContent()}
        showCloseButton={false}
        showSubmitButton={false}
        size="4xl"
      />
      <Modal
        isOpen={!!modals.linkListing}
        onClose={onLinkListingClose}
        title="Link Item"
        content={renderLinkListingModalContent()}
        showCloseButton={false}
        showSubmitButton={false}
        size="4xl"
      />
      <Modal
        isOpen={!!modals.tagListing}
        onClose={onTagListingClose}
        title="Tag Sale"
        content={renderTagListingModalContent()}
        showCloseButton={false}
        showSubmitButton={false}
        size="2xl"
      />
      <Modal
        isOpen={modals.migrateImage}
        onClose={onMigrateImageModalClose}
        title="Migrate Artist Image"
        content={renderMigrateImageModalContent()}
        showCloseButton={false}
        showSubmitButton={false}
        size="2xl"
      />
      <Modal
        isOpen={pendingConfirmModalOpen}
        onClose={() => {
          setPendingConfirmModalOpen(false);
          setPendingConfirmAction(null);
        }}
        title="Confirm Tagging"
        content={renderPendingConfirmModalContent()}
        showCloseButton={false}
        showSubmitButton={false}
        size="4xl"
      />
    </>
  );

  if (!artist && !artistLoading) {
    return (
      <Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh">
        <EmptyState header="Artist Not Found" description="The requested artist could not be found." showButton={false} />
      </Box>
    );
  }

  const tabLabels = [
    <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="artist-items-tab">
      <Icon as={IoGridOutline} />
      <Text>Items</Text>
    </Flex>,
    <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="artist-trading-tab">
      <Icon as={IoStorefrontOutline} />
      <Text>Trading Floor</Text>
    </Flex>,
    <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="artist-sales-tab">
      <Icon as={RiAuctionLine} />
      <Text>Past Sales</Text>
    </Flex>,
    <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="artist-untagged-tab">
      <Icon as={MdPendingActions} />
      <Text>Untagged Sales</Text>
    </Flex>,
    <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }} key="artist-stats-tab">
      <Icon as={ImStatsDots} />
      <Text>Stats</Text>
    </Flex>
  ];

  const tabContent = [
    renderArtistItems(),
    renderArtistTradingFloor(),
    renderArtistListings(),
    renderArtistPendingListings(),
    renderArtistStats()
  ];

  return (
    <>
      <Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh">
        {renderBanner()}
        <TabGroup labels={tabLabels} content={tabContent} startingTab={initialTab} onChange={handleTabChange} />
      </Box>
      {renderModals()}
    </>
  );
}
